import {defineComponent} from "vue";
import {DateTime, Duration} from "luxon";
import {isDate, isString} from "@/utils/TypeUtil";
import axios from "axios";
import {DurationObjectUnits} from "luxon/src/duration";

/** DateTime として変換できる型 */
export type DateTimeInput = DateTime | Date | string;

/**
 * DateTime として変換できる型を DateTime に変換します
 *
 * @param mayDateTime DateTime として変換できる値
 */
export function toDateTime(mayDateTime: DateTimeInput | null): DateTime | null {
  if(isString(mayDateTime)) {
    return DateTime.fromISO(mayDateTime);
  } else if(isDate(mayDateTime)) {
    return DateTime.fromJSDate(mayDateTime);
  } else {
    return mayDateTime;
  }
}

/**
 * エラーをフォーマットします
 *
 * @param err エラー
 * @param title タイトル
 */
export function formatError(err: unknown, title: string): string | null {
  if(axios.isAxiosError(err)) {
    const resp = err.response;
    const url = resp?.config?.url ?? '';
    const lines = (resp?.data?.detail?.trim()?.split(/\n/) ?? [])
        .map((e: string) => {
          // データ実装
          if(url.startsWith('/data_implementations/') &&
              e === 'local_id: should_unique_in_scope_of_controller_id') {
            return '内部IDが重複しています';
          }

          // データ項目実装
          if(url.startsWith('/data_property_implementations/') &&
              e === 'local_id: should_unique_in_scope_of_data_implementation_id') {
            return '内部IDが重複しています';
          }

          // サービス実装
          if(url.startsWith('/service_implementations/') &&
              e === 'local_id: should_unique_in_scope_of_controller_id') {
            return '内部IDが重複しています';
          }

          // プロセス実装
          if(url.startsWith('/process_implementations/') &&
              e === 'local_id: should_unique_in_scope_of_service_implementation_id') {
            return '内部IDが重複しています';
          }

          // イベント実装
          if(url.startsWith('/event_implementations/') &&
              e === 'local_id: should_unique_in_scope_of_process_implementation_id') {
            return '内部IDが重複しています';
          }

          // その他
          return `不明なエラー (${e})`;
        })
        .map((e: string) => `・ ${e}`);

    lines.unshift(title);
    return lines.join("\n");
  }
  return `不明なエラー (${err})`;
}

/** 数値フォーマッター */
const numberFormatter = new Intl.NumberFormat(navigator.language);

export default defineComponent({
  methods: {
    formatDate(date: DateTimeInput) {
      const m = toDateTime(date)
      if(m === undefined || m === null) {
        return '';
      }
      if (m.isValid) {
        return m.toFormat('yyyy/LL/dd');
      } else {
        return '';
      }
    },
    formatDateTime(date: DateTimeInput) {
      const m = toDateTime(date)
      if(m === undefined || m === null) {
        return '';
      }
      if (m.isValid) {
        return m.toFormat('yyyy/LL/dd HH:mm:ss');
      } else {
        return '';
      }
    },
    formatDateTimeRelative(date: DateTimeInput) {
      const m = toDateTime(date)
      if(m === undefined || m === null) {
        return '';
      }
      if (m.isValid) {
        const units = ['years', 'months', 'days', 'hours', 'minutes'] as (keyof DurationObjectUnits)[];
        const diff = m.diffNow(units);
        if(Math.abs(diff.toMillis()) > 60000) {
          const diffObj = diff.toObject();
          for (const unit of units) {
            const value = diffObj[unit] ?? 0;
            const absValue = Math.abs(value);
            if (absValue > 0) {
              return Duration.fromObject({[unit]: absValue}).toHuman() + (value > 0 ? '後' : '前');
            }
          }
        }
        return 'たった今';
      } else {
        return '';
      }
    },
    formatNumber(n: number) {
      return numberFormatter.format(n);
    },
    formatError(err: unknown, title: string): string | null {
      return formatError(err, title);
    },
    escapeHtml(str: string): string {
      return str.replace(/[&'`"<>]/g, m => {
        switch(m) {
          case '&': return '&amp;';
          case "'": return '&#x27;';
          case '`': return '&#x60;';
          case '"': return '&quot;';
          case '<': return '&lt;';
          case '>': return '&gt;';
          default:  return m;
        }
      });
    }
  }
});
